import { Shield, Lock, Activity, Cloud, FileText, AlertTriangle, CheckCircle, Zap, Users, Target, Search, ShoppingCart, Globe } from 'lucide-react';

export const BUSINESS_NAME = "AGRANDINTL";
export const TAGLINE = "Cybersecurity consulting for retail, e-commerce, and cloud-based businesses";

export const COMPANY_DETAILS = {
  name: "AGRANDINTL",
  location: "Washington DC, Virginia, Maryland",
  phone: "+1 202 630 6862",
  email: "contact@agrandintl.com",
  founderEmail: "fabiyananik@agrandintl.com",
  founderName: "Fabiyan Anik",
  description: "Practical cybersecurity solutions for small to mid-size companies. We provide risk assessments, compliance support, and strategic security implementation across the DMV area."
};

export const NAV_LINKS = [
  { name: "Home", href: "/" },
  { name: "About", href: "/about" },
  { name: "Cybersecurity", href: "/services" },
  { name: "Complytics", href: "/complytics" },
  { name: "Compliance", href: "/security-compliance" },
  { name: "Industries", href: "/industries" },
  { name: "Contact", href: "/contact" },
];

export const PILLARS = [
  {
    id: "cybersecurity",
    title: "Cybersecurity Consulting",
    description: "Expert analysis to identify security risks, fix vulnerabilities, and build a stronger defense for your business.",
    icon: Shield,
    href: "/services"
  },
  {
    id: "complytics",
    title: "Complytics AI",
    description: "Automated compliance monitoring software to help you stay compliant with PCI and SOC2 standards in real-time.",
    icon: Zap,
    href: "/complytics"
  },
  {
    id: "operations",
    title: "Secure Operations",
    description: "Strategic consulting to help your business operate with confidence and resilience as you scale.",
    icon: Users,
    href: "/industries"
  }
];

export const SERVICES = [
  {
    id: "vulnerability-assessment",
    title: "Cybersecurity Risk Audit",
    description: "A professional security baseline for your business. Starting at $299.",
    icon: Shield,
    details: "A comprehensive review of your digital infrastructure to find and prioritize security gaps.",
    benefits: [
      "Technical Vulnerability Scan",
      "Quantified Security Risk Score",
      "Executive Summary Report",
      "Prioritized Remediation Plan"
    ]
  },
  {
    id: "penetration-testing",
    title: "Penetration Testing",
    description: "Simulate real-world attacks to test your existing defenses.",
    icon: Target,
    details: "Our experts try to get into your systems just like a hacker would to find hidden flaws.",
    benefits: [
      "Attack Simulations",
      "Security Control Testing",
      "Logical Flaw Discovery",
      "Hardening Recommendations"
    ]
  },
  {
    id: "cloud-security",
    title: "Cloud Security",
    description: "Secure your AWS, Azure, or Google Cloud environment.",
    icon: Cloud,
    details: "We help you set up your cloud systems correctly to prevent data leaks and unauthorized access.",
    benefits: [
      "Cloud Config Review",
      "IAM & Access Control",
      "Automated Security Checks",
      "Data Leak Prevention"
    ]
  },
  {
    id: "pci-compliance",
    title: "Compliance Support",
    description: "Meet PCI DSS, SOC2, and other regulatory standards.",
    icon: FileText,
    details: "We guide you through the requirements and help you get audit-ready with minimal friction.",
    benefits: [
      "Guided Compliance Process",
      "Gap Analysis",
      "Evidence Collection",
      "Regulatory Risk Reduction"
    ]
  }
];

export const INDUSTRIES = [
  {
    id: "retail",
    title: "Retail Businesses",
    description: "Securing payment systems and protecting customer data for businesses handling payments.",
    challenges: ["PCI compliance", "POS system security", "Customer data protection"],
    solution: "We monitor your payment networks and test your systems to ensure every transaction is secure.",
    icon: ShoppingCart
  },
  {
    id: "ecommerce",
    title: "E-commerce Stores",
    description: "Protecting Shopify and custom online platforms from account takeovers and payment fraud.",
    challenges: ["Secure checkout", "Account protection", "DDoS mitigation"],
    solution: "We harden your web store and monitor for suspicious activity to protect your revenue.",
    icon: Globe
  },
  {
    id: "smb",
    title: "Small to Mid-Size Companies",
    description: "Growing organizations scaling their digital footprint and managing risk securely.",
    challenges: ["Limited security staff", "Scaling infrastructure", "Employee awareness"],
    solution: "We provide enterprise-grade security expertise tailored to the budget and needs of growing businesses.",
    icon: Users
  },
  {
    id: "cloud-native",
    title: "Cloud-Based Businesses",
    description: "SaaS platforms and cloud-native infrastructure requiring hardened protection.",
    challenges: ["API security", "Cloud misconfigurations", "Identity management"],
    solution: "We audit your cloud environment and set up automated checks to keep your platform safe.",
    icon: Cloud
  }
];

export const WHY_CHOOSE_US = [
  {
    title: "Retail-First Intelligence",
    description: "Our team specializes in the unique security challenges of high-volume retail and POS networks.",
    icon: Users
  },
  {
    title: "Cloud-Native Expertise",
    description: "We bring deep experience in hardening complex AWS, Azure, and Google Cloud architectures.",
    icon: Zap
  },
  {
    title: "Intelligence-Led Defense",
    description: "We don't just scan; we simulate real-world attacks to build truly resilient systems.",
    icon: Target
  },
  {
    title: "Hardened Infrastructure",
    description: "We stand behind our work with a commitment to significantly reducing your attack surface.",
    icon: CheckCircle
  }
];

export const FAQS = [
  {
    question: "What is cybersecurity consulting?",
    answer: "Cybersecurity consulting involves expert analysis and guidance to help businesses identify, assess, and mitigate digital risks. We provide the strategy and tools needed to protect your data, systems, and reputation."
  },
  {
    question: "How does AGRANDINTL handle data confidentiality?",
    answer: "We operate under strict NDAs and use encrypted communication channels for all client engagements. Our team follows the principle of least privilege, ensuring your sensitive data remains protected throughout our assessment."
  },
  {
    question: "What certifications does your team hold?",
    answer: "Our consultants hold industry-recognized certifications including CISSP, OSCP, CISM, and CEH. We are also qualified to perform audits for PCI DSS, SOC2, and HIPAA compliance."
  },
  {
    question: "Can you help with emergency incident response?",
    answer: "Yes, we provide priority incident support for our clients. If you suspect a breach, our team is available to assist with containment and forensic investigation."
  },
  {
    question: "How often should we perform penetration testing?",
    answer: "Industry best practices and compliance frameworks (like PCI DSS) typically require annual testing or after any significant infrastructure changes. For high-risk environments, we recommend semi-annual assessments."
  }
];

export const CASE_STUDIES = [
  {
    id: "retail-security",
    title: "Securing a Retail Chain's Payment Network",
    industry: "Retail",
    problem: "A retail business with multiple locations was concerned about payment security and potential data breaches.",
    solution: "We performed a full security audit, fixed network vulnerabilities, and set up continuous monitoring for all payment terminals.",
    result: "Achieved full PCI compliance and zero security incidents since the project completion.",
    image: "https://images.unsplash.com/photo-1441986300917-64674bd600d8?auto=format&fit=crop&q=80&w=800"
  },
  {
    id: "cloud-hardening",
    title: "Cloud Security for a Growing SaaS Platform",
    industry: "SaaS",
    problem: "A software company needed to ensure their customer data was protected in their cloud environment.",
    solution: "We audited their cloud setup, fixed permission issues, and implemented automated security checks.",
    result: "Successfully passed their security review and improved their overall security score by 60%.",
    image: "https://images.unsplash.com/photo-1451187580459-43490279c0fa?auto=format&fit=crop&q=80&w=800"
  }
];

export const BLOG_POSTS = [
  {
    id: "security-trends-2026",
    title: "Security Trends in 2026: What Business Owners Need to Know",
    excerpt: "Learn about the latest security threats and how to protect your business from evolving digital risks.",
    date: "March 15, 2026",
    author: "Security Consultant",
    category: "Security Tips",
    image: "https://images.unsplash.com/photo-1550751827-4bd374c3f58b?auto=format&fit=crop&q=80&w=800"
  },
  {
    id: "pci-compliance-guide",
    title: "A Simple Guide to PCI Compliance for Retailers",
    excerpt: "We break down the key requirements of PCI compliance and how to make the process easier for your business.",
    date: "February 28, 2026",
    author: "Compliance Expert",
    category: "Compliance",
    image: "https://images.unsplash.com/photo-1563986768609-322da13575f3?auto=format&fit=crop&q=80&w=800"
  }
];

export const SERVICE_COMPARISON = {
  features: [
    "Security Risk Audit",
    "Monthly Monitoring",
    "Vulnerability Scanning",
    "Compliance Support",
    "Cloud Hardening",
    "Dedicated Support",
    "Custom Fix Roadmap"
  ],
  plans: [
    {
      name: "Audit Only",
      description: "A one-time professional security check.",
      features: [true, false, true, true, false, false, true]
    },
    {
      name: "Monthly Protection",
      description: "Ongoing monitoring and support.",
      features: [true, true, true, true, true, false, true]
    },
    {
      name: "Full Partnership",
      description: "Complete security management.",
      features: [true, true, true, true, true, true, true]
    }
  ]
};

export const ASSESSMENT_QUESTIONS = [
  {
    id: "mfa",
    question: "Do you use Multi-Factor Authentication (MFA) for all employee accounts?",
    options: [
      { label: "Yes, on all systems", score: 10, risk: "Low" },
      { label: "Only on critical systems", score: 5, risk: "Medium" },
      { label: "No, we don't use MFA", score: 0, risk: "High" }
    ],
    recommendation: "Implement MFA across all access points to reduce the risk of account takeover by 99%."
  },
  {
    id: "backups",
    question: "How often do you perform and test your data backups?",
    options: [
      { label: "Daily and tested monthly", score: 10, risk: "Low" },
      { label: "Weekly but rarely tested", score: 5, risk: "Medium" },
      { label: "Infrequently or never", score: 0, risk: "High" }
    ],
    recommendation: "Ensure daily automated backups with regular restoration tests to guarantee business continuity during ransomware attacks."
  },
  {
    id: "training",
    question: "Do employees receive regular security awareness training?",
    options: [
      { label: "Yes, quarterly or more", score: 10, risk: "Low" },
      { label: "Once a year", score: 5, risk: "Medium" },
      { label: "No formal training", score: 0, risk: "High" }
    ],
    recommendation: "Human error is the #1 cause of breaches. Regular phishing simulations and training are essential."
  },
  {
    id: "vulnerability",
    question: "When was your last professional vulnerability assessment or pentest?",
    options: [
      { label: "Within the last 6 months", score: 10, risk: "Low" },
      { label: "Over a year ago", score: 5, risk: "Medium" },
      { label: "Never", score: 0, risk: "High" }
    ],
    recommendation: "Regular penetration testing identifies exploitable gaps before malicious actors do."
  },
  {
    id: "incident_plan",
    question: "Do you have a documented Incident Response Plan?",
    options: [
      { label: "Yes, and it's tested", score: 10, risk: "Low" },
      { label: "Yes, but not tested", score: 5, risk: "Medium" },
      { label: "No plan in place", score: 0, risk: "High" }
    ],
    recommendation: "A tested incident response plan significantly reduces the cost and impact of a security breach."
  }
];

export const TESTIMONIALS = [
  {
    quote: "AGRANDINTL identified critical gaps in our retail POS network that our previous provider missed. Their audit was clear, professional, and gave us a direct path to remediation.",
    author: "Franchise Owner, M. Roberts",
    role: "Washington DC",
    company: "Metro Retail Group"
  },
  {
    quote: "The continuous monitoring gives our team peace of mind. We now have a clear view of our cloud security posture without the overhead of a full-time security hire.",
    author: "SaaS Founder, S. Chen",
    role: "Reston, Virginia",
    company: "CloudTech Solutions"
  },
  {
    quote: "We needed help with PCI compliance for our multi-state expansion and they made the whole process straightforward. Highly recommended for any business handling customer payments.",
    author: "Finance Director, J. Thompson",
    role: "Baltimore, Maryland",
    company: "Global Goods Logistics"
  }
];
